/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Modal } from "src/assets/imports/antdComponents";

interface IWaveSelectorModalProps {
	waves: Record<string, string>;
	visibleWaves: string[];
	setVisibleWaves: React.Dispatch<React.SetStateAction<string[]>>;
	onClose: () => void;
	waveMarkers?: Record<string, string>;
}

/* -------------------- Marker Symbol Component -------------------- */
const MarkerSymbol: React.FC<{ markerType: string; color: string; size?: number; active?: boolean }> = ({ markerType, color, size = 12, active = true }) => {
	const opacity = active ? 1 : 0.3;
	const halfSize = size / 2;

	switch (markerType) {
		case "Square":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<rect x="1" y="1" width={size - 2} height={size - 2} fill={color} opacity={opacity} />
				</svg>
			);
		case "Triangle":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<polygon points={`${halfSize},1 ${size - 1},${size - 1} 1,${size - 1}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Diamond":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<polygon points={`${halfSize},1 ${size - 1},${halfSize} ${halfSize},${size - 1} 1,${halfSize}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Cross":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<line x1="2" y1="2" x2={size - 2} y2={size - 2} stroke={color} strokeWidth="2" opacity={opacity} />
					<line x1={size - 2} y1="2" x2="2" y2={size - 2} stroke={color} strokeWidth="2" opacity={opacity} />
				</svg>
			);
		case "Pin":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<circle cx={halfSize} cy={halfSize - 1} r={halfSize - 2} fill={color} opacity={opacity} />
					<polygon points={`${halfSize},${size - 1} ${halfSize - 2},${halfSize} ${halfSize + 2},${halfSize}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Arrow":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<polygon points={`${halfSize},1 ${size - 1},${size - 1} ${halfSize},${size - 3} 1,${size - 1}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Circle":
		default:
			return <span className="flex-shrink-0 rounded-full" style={{ width: size, height: size, backgroundColor: color, opacity }} />;
	}
};

const WaveSelectorModal: React.FC<IWaveSelectorModalProps> = ({ onClose, setVisibleWaves, visibleWaves, waves, waveMarkers }) => {
	const waveEntries = Object.entries(waves).map(([label, color]) => ({
		label,
		color,
	}));

	const toggleWave = (label: string) => {
		setVisibleWaves((prev) => (prev.includes(label) ? prev.filter((w) => w !== label) : [...prev, label]));
	};

	return (
		<Modal open onCancel={onClose} footer={null} maskClosable width={500} styles={{ body: { padding: "1.25rem 0 0" } }} destroyOnHidden>
			<div className="flex flex-col max-h-[70vh]">
				{/* List */}
				<div className="flex-1 overflow-y-auto p-2">
					{waveEntries.map((wave) => {
						const active = visibleWaves?.includes(wave.label);
						const markerType = waveMarkers?.[wave.label];

						return (
							<div
								key={wave.label}
								onClick={() => toggleWave(wave.label)}
								className="grid grid-cols-[16px_minmax(0,1fr)] items-center gap-2 p-1 cursor-pointer hover:bg-gray-50 rounded-md">
								{/* Marker Symbol */}
								<MarkerSymbol markerType={markerType || "Circle"} color={wave.color} size={12} active={active} />

								{/* Label */}
								<span className={`text-[11px] break-words leading-snug ${active ? "text-[#1F3A6D]" : "text-gray-400"}`} title={wave.label}>
									{wave.label}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</Modal>
	);
};

export default React.memo(WaveSelectorModal);
